import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { PointerChartType } from "../../store/helper";

type Props = {
  title: string;
  lowLimit: number;
  highLimit: number;
  value: number;
  initialValue?: number;
  rangeMessage?: string;
  type: number;
};

const PointerChart = ({
  title,
  lowLimit,
  highLimit,
  value,
  initialValue,
  rangeMessage,
  type,
}: Props) => {
  const theme = useTheme();

  const range = highLimit - lowLimit;
  const pointerValue = value - lowLimit;

  const getColorCodingValues = (value: number) => {
    if (type === PointerChartType.HR_zonesTab) {
      return {
        bar: theme.colorTokens.P[10],
        pointer: theme.colorTokens.P[60],
        category: "",
      };
    }

    if (type === PointerChartType.reverseValues) {
      value = 100 - value;
    }

    if (type === PointerChartType.recovery) {
      return {
        bar: theme.colorTokens.S[10],
        pointer: theme.colorTokens.S[50],
        category: "Very good",
      };
    }

    if (value < 0) {
      return {
        bar: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        pointer: "transparent",
        category: "",
      };
    } else if (value < 20) {
      return {
        bar: theme.colorTokens.D[10],
        pointer: theme.colorTokens.D[60],
        category: "Very bad",
      };
    } else if (value < 40) {
      return {
        bar: theme.colorTokens.W[10],
        pointer: theme.colorTokens.W[60],
        category: "Bad",
      };
    } else if (value < 60) {
      return {
        bar: theme.colorTokens.P[10],
        pointer: theme.colorTokens.P[60],
        category: "Good",
      };
    } else if (value < 80) {
      return {
        bar: theme.colorTokens.S[10],
        pointer: theme.colorTokens.S[50],
        category: "Very good",
      };
    } else if (value <= 100) {
      return {
        bar: theme.colorTokens.S[20],
        pointer: theme.colorTokens.S[60],
        category: "Excellent",
      };
    } else {
      return {
        bar: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        pointer: theme.colorTokens.N[80],
        category: "",
      };
    }
  };

  const curPercentage = (pointerValue * 100) / range;

  return (
    <div style={styles.wrapper(theme, initialValue !== undefined)}>
      <div style={styles.container(theme, initialValue !== undefined)}>
        <div style={styles.titleContainer()}>
          <span style={styles.title(theme)}>{title}</span>
          <span style={styles.percentage(theme)}>
            {getColorCodingValues(curPercentage).category}
          </span>
        </div>
        <div style={styles.table}>
          <div style={styles.text(theme)}>{lowLimit}</div>
          <div
            style={styles.barContainer(getColorCodingValues(curPercentage).bar)}
          >
            {value !== -1 && (
              <div
                style={styles.barInner(
                  lowLimit === highLimit
                    ? "transparent"
                    : getColorCodingValues(curPercentage).pointer,
                  `${curPercentage}%`
                )}
              >
                {!(lowLimit === highLimit) && (
                  <span style={styles.pointer(theme)}>{value}</span>
                )}
              </div>
            )}
          </div>
          <div style={styles.text(theme)}>{highLimit}</div>
        </div>
        <div style={styles.rangeContainer(theme)}>
          <div style={styles.rangeText(theme)}>{rangeMessage}</div>
        </div>
      </div>
      {initialValue !== undefined ? (
        <div style={styles.initialValueContainer(theme)}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={styles.initialValueDot(theme)} />
            <div style={styles.initialValueLabel(theme)}>Initial recovery</div>
          </div>
          <div style={styles.initialValueValue(theme)}>
            {initialValue}
            <span style={styles.initialValueLabel(theme)}>%</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const styles = {
  // The grey sheet is the backdrop of the "Initial …" row under the card;
  // without that row there is nothing to back, so the card stands alone
  // (otherwise a 16px strip of grey showed above it).
  wrapper: (theme: any, hasInitialValue: boolean) => ({
    borderRadius: "8px",
    backgroundColor: hasInitialValue ? theme.colorTokens.N[40] : "transparent",
  }),
  container: (theme: any, hasInitialValue: boolean) => ({
    width: "100%",
    marginTop: hasInitialValue ? "16px" : 0,
    padding: "16px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    cursor: "pointer",
    position: "relative" as "relative",
  }),
  titleContainer: () => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }),
  title: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  percentage: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  table: {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  barContainer: (barColor: string) => ({
    height: "6px",
    flex: 1,
    backgroundColor: barColor,
    borderRadius: "38px",
    position: "relative" as "relative",
  }),
  barInner: (borderColor: string, customWidth: string) => ({
    width: "16px",
    height: "6px",
    backgroundColor: borderColor,
    borderRadius: "38px",
    position: "absolute" as "absolute",
    top: "50%",
    left: customWidth,
    transform: "translate(-50%, -50%)",
  }),
  text: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  pointer: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    width: "30px",
    textAlign: "center" as "center",
    position: "absolute" as "absolute",
    top: "-24px",
    right: "-23px",
    transform: "translateX(-50%)",
  }),
  rangeContainer: (theme: any) => ({
    marginTop: "24px",
    marginBottom: "8px",
    border: `1px dashed ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    position: "relative" as "relative",
  }),
  rangeText: (theme: any) => ({
    width: "130px",
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "-10px",
    left: "50%",
    transform: "translateX(-50%)",
    justifyContent: "center",
    alignItems: "center",
  }),
  initialValueContainer: (theme: any) => ({
    padding: "10px 12px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  }),
  initialValueDot: (theme: any) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.W[60],
  }),
  initialValueLabel: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  initialValueValue: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};

export default PointerChart;
